import { useTranslation } from 'react-i18next';
import type {
  EstateAvailability,
  FlatfinderData,
  FlatfinderEstate,
} from 'realitiq-utils/src/flatfinder';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { getFlatfinderAvailabilityOptions, getFlatfinderLanguage } from './display';

type NumberRange = { min: string; max: string };

export type FlatfinderFilters = {
  availability: EstateAvailability | 'all';
  marketType: 'all' | 'buy' | 'rent';
  rooms: NumberRange;
  floor: NumberRange;
  area: NumberRange;
  currency: string;
  purchasePrice: NumberRange;
  rentalPrice: NumberRange;
};

function createEmptyNumberRange(): NumberRange {
  return { min: '', max: '' };
}

export function createInitialFlatfinderFilters(): FlatfinderFilters {
  return {
    availability: 'all',
    marketType: 'all',
    rooms: createEmptyNumberRange(),
    floor: createEmptyNumberRange(),
    area: createEmptyNumberRange(),
    currency: 'all',
    purchasePrice: createEmptyNumberRange(),
    rentalPrice: createEmptyNumberRange(),
  };
}

function numberMatchesRange(value: number | null, range: NumberRange) {
  const hasMinimum = range.min.trim() !== '';
  const hasMaximum = range.max.trim() !== '';
  if (!hasMinimum && !hasMaximum) return true;
  if (value === null) return false;

  const minimum = Number(range.min);
  const maximum = Number(range.max);
  return (!hasMinimum || value >= minimum) && (!hasMaximum || value <= maximum);
}

export function estateMatchesFlatfinderFilters(
  estate: FlatfinderEstate,
  filters: FlatfinderFilters
) {
  if (filters.availability !== 'all' && estate.availability !== filters.availability) return false;
  if (
    filters.marketType !== 'all' &&
    (filters.marketType === 'rent'
      ? estate.marketType !== 'rent' && estate.marketType !== 'lease'
      : estate.marketType !== 'buy')
  ) {
    return false;
  }
  if (filters.currency !== 'all' && estate.currency !== filters.currency) return false;
  if (!numberMatchesRange(estate.rooms, filters.rooms)) return false;
  if (!numberMatchesRange(estate.floor, filters.floor)) return false;
  if (!numberMatchesRange(estate.livingArea, filters.area)) return false;

  const priceRange = estate.marketType === 'buy' ? filters.purchasePrice : filters.rentalPrice;
  return numberMatchesRange(estate.price, priceRange);
}

function NumberRangeInputs({
  label,
  range,
  onChange,
}: {
  label: string;
  range: NumberRange;
  onChange: (range: NumberRange) => void;
}) {
  const { t } = useTranslation();
  return (
    <fieldset>
      <legend className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </legend>
      <div className="grid grid-cols-2 gap-2">
        <Input
          aria-label={`${label} ${t('minimum')}`}
          size="sm"
          wrapperClassName="min-w-0"
          inputMode="decimal"
          min="0"
          placeholder={t('minimumShort')}
          type="number"
          value={range.min}
          onChange={(event) => onChange({ ...range, min: event.target.value })}
        />
        <Input
          aria-label={`${label} ${t('maximum')}`}
          size="sm"
          wrapperClassName="min-w-0"
          inputMode="decimal"
          min="0"
          placeholder={t('maximumShort')}
          type="number"
          value={range.max}
          onChange={(event) => onChange({ ...range, max: event.target.value })}
        />
      </div>
    </fieldset>
  );
}

export function FlatfinderFilterFields({
  data,
  currencies,
  filters,
  onChange,
}: {
  data: FlatfinderData;
  currencies: string[];
  filters: FlatfinderFilters;
  onChange: (filters: FlatfinderFilters) => void;
}) {
  const language = getFlatfinderLanguage();
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Select
          label={t('status')}
          size="sm"
          disableClear
          readOnlySearchInput
          options={[
            { id: 'all', title: t('all') },
            ...getFlatfinderAvailabilityOptions(
              language,
              filters.marketType === 'all'
                ? data.estates.map((estate) => estate.marketType)
                : [filters.marketType]
            ),
          ]}
          value={filters.availability ?? 'all'}
          onChange={(event) =>
            onChange({
              ...filters,
              availability: event.target.value as FlatfinderFilters['availability'],
            })
          }
        />
        <Select
          label={t('marketType')}
          size="sm"
          disableClear
          readOnlySearchInput
          options={[
            { id: 'all', title: t('all') },
            { id: 'buy', title: t('purchase') },
            { id: 'rent', title: t('rent') },
          ]}
          value={filters.marketType}
          onChange={(event) =>
            onChange({
              ...filters,
              marketType: event.target.value as FlatfinderFilters['marketType'],
              availability: 'all',
            })
          }
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <NumberRangeInputs
          label={t('rooms')}
          range={filters.rooms}
          onChange={(rooms) => onChange({ ...filters, rooms })}
        />
        <NumberRangeInputs
          label={t('floor')}
          range={filters.floor}
          onChange={(floor) => onChange({ ...filters, floor })}
        />
        <NumberRangeInputs
          label={`${t('area')} (m²)`}
          range={filters.area}
          onChange={(area) => onChange({ ...filters, area })}
        />
        <Select
          label={t('currency')}
          size="sm"
          disableClear
          readOnlySearchInput
          options={[
            { id: 'all', title: t('all') },
            ...currencies.map((currency) => ({ id: currency, title: currency })),
          ]}
          value={filters.currency}
          onChange={(event) =>
            onChange({
              ...filters,
              currency: event.target.value as FlatfinderFilters['currency'],
            })
          }
        />
        <NumberRangeInputs
          label={t('purchasePriceSection')}
          range={filters.purchasePrice}
          onChange={(purchasePrice) => onChange({ ...filters, purchasePrice })}
        />
        <NumberRangeInputs
          label={t('rentalPrice')}
          range={filters.rentalPrice}
          onChange={(rentalPrice) => onChange({ ...filters, rentalPrice })}
        />
      </div>
    </div>
  );
}
